import React, { useMemo } from 'react';
import {
  DataGrid,
  GridColDef,
  CellTypes,
  Button,
  DataGridProps,
} from '@fashionnovateam/nova-ui-toolkit';
import { startCase } from 'lodash';
import { Link } from 'react-router-dom';
import { Influencer } from '../../Lib/Api/influencer';
import getApplicationStatus from '../../Lib/Utils/InfluencerForm/get-application-status';
import {
  hasAccessTo,
  hasPermissionTo,
  RolesType,
  Actions,
} from '../../Lib/Constants/roles';
import ApplicationStatus from '../../Lib/Constants/vendor-application-status';

type InfluencerRow = {
  id: number;
  first_name: CellTypes.DataGridCellCompanyParams;
  application_status: CellTypes.DataGridCellBadgeParams;
  netsuite_id: CellTypes.DataGridCellStringParams;
  email: CellTypes.DataGridCellEmailParams;
  last_logged_in_at: CellTypes.DataGridCellDateTimeParams;
  country: CellTypes.DataGridCellStringParams;
  platform: CellTypes.DataGridCellBadgeParams;
  handle: CellTypes.DataGridCellStringParams;
  view: CellTypes.DataGridCellCustomParams;
  archive: CellTypes.DataGridCellCustomParams;
  netsuite_status: CellTypes.DataGridCellBadgeParams | null;
};

const columns: GridColDef[] = [
  {
    field: 'first_name',
    headerName: 'Influencer Name',
    render: 'company',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'application_status',
    headerName: 'Status',
    render: 'badge',
    flex: 2,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'netsuite_status',
    headerName: 'Netsuite Status',
    render: 'badge',
    flex: 1,
    sortable: false,
    minWidth: 120,
    disableColumnMenu: true,
  },
  {
    field: 'netsuite_id',
    headerName: 'Netsuite ID',
    render: 'string',
    display: 'flex',
    flex: 1,
    sortable: false,
    minWidth: 85,
    disableColumnMenu: true,
  },
  {
    field: 'email',
    headerName: 'Email',
    render: 'email',
    flex: 2,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'last_logged_in_at',
    headerName: 'Last Login',
    render: 'dateTime',
    flex: 1,
    sortable: true,
    minWidth: 90,
    disableColumnMenu: true,
  },
  {
    field: 'country',
    headerName: 'Country',
    render: 'string',
    flex: 1,
    sortable: false,
    minWidth: 75,
    display: 'flex',
    disableColumnMenu: true,
  },
  {
    field: 'platform',
    headerName: 'Platform',
    render: 'badge',
    flex: 1,
    sortable: false,
    minWidth: 150,
    display: 'flex',
    disableColumnMenu: true,
  },
  {
    field: 'handle',
    headerName: 'Handle',
    render: 'string',
    flex: 1,
    sortable: false,
    minWidth: 150,
    display: 'flex',
    disableColumnMenu: true,
  },
  {
    field: 'view',
    headerName: '',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 50,
    display: 'flex',
    disableColumnMenu: true,
  },
  {
    field: 'archive',
    headerName: '',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 50,
    display: 'flex',
    disableColumnMenu: true,
  },
];

type Props = {
  rows: Influencer[];
  count: number;
  loading: boolean;
  params: {
    vendorFilters: any;
    sortBy: string;
    sortOrder: 'ASC' | 'DESC';
    page: number;
  };
  currentUser: { role: RolesType };
  archiveInfluencer: (id: number, archive: boolean, params: any) => void;
} & Omit<DataGridProps<InfluencerRow>, 'columns'>;

function SocialMediaDataGrid({
  rows,
  count,
  loading,
  params,
  currentUser,
  archiveInfluencer,
  ...datagridProps
}: Props) {
  const applicationStatusColorMapping: {
    [key: string]: 'green' | 'yellow' | 'red' | 'neutral';
  } = {
    REJECTED: 'red',
    APPROVED: 'green',
    'PENDING APPROVAL': 'yellow',
    'PENDING ACCOUNTING APPROVAL': 'yellow',
    'APPLICATION PENDING': 'yellow',
    ARCHIVE: 'neutral',
  };

  const getInfluencerName = (influencer: Influencer) => {
    if (!influencer.first_name) {
      return startCase(influencer.vendor.company_name);
    }
    return `${startCase(influencer.first_name)} ${startCase(
      influencer.last_name,
    )}`;
  };

  const canArchive = useMemo(
    () =>
      hasPermissionTo(
        'social-media-console',
        currentUser.role,
        Actions.UPDATE,
        'ARCHIVE',
      ),
    [currentUser.role],
  );

  const transformedRows = useMemo(
    () =>
      rows.map((influencer) => {
        const applicationStatus = getApplicationStatus(influencer?.vendor);
        const isArchived = influencer?.vendor?.application_archived_at != null;
        const isActive = influencer?.vendor?.inactive === false;
        const hasNetsuiteId = !!influencer?.vendor?.internal_id;
        const hasInactiveStatus = influencer?.vendor?.inactive !== null;

        const showArchiveButton =
          canArchive &&
          (isArchived ||
            applicationStatus ===
              ApplicationStatus.PENDING_ACCOUNTING_APPROVAL ||
            applicationStatus === ApplicationStatus.APPLICATION_PENDING ||
            applicationStatus === ApplicationStatus.REJECTED);

        const displayStatus = isArchived
          ? ApplicationStatus.ARCHIVE
          : applicationStatus;

        return {
          id: influencer.id,
          first_name: {
            name: getInfluencerName(influencer),
          },
          application_status: {
            children: startCase(displayStatus.toLowerCase()),
            contrast: 'low',
            color: applicationStatusColorMapping[startCase(displayStatus)],
          },
          netsuite_status:
            hasNetsuiteId && hasInactiveStatus
              ? {
                  children: isActive ? 'Active' : 'Inactive',
                  contrast: 'low',
                  color: isActive ? 'green' : 'neutral',
                }
              : null,
          netsuite_id: influencer.vendor.internal_id ?? '-',
          email: influencer.email,
          last_logged_in_at: {
            activity: true,
            time: true,
            date: influencer.last_logged_in_at,
          },
          country: influencer?.vendor?.country,
          platform: {
            children: influencer?.vendor?.influencer_handles?.find(
              (h) => h.primary,
            )?.social_media_platform,
            contrast: 'low',
            color: 'neutral',
          },
          handle:
            influencer.vendor?.influencer_handles?.find((h) => h.primary)
              ?.social_media_handle || '',
          view: hasAccessTo(
            'users/:id/influencer-payee-information',
            currentUser.role,
          ) ? (
            <Link to={`/users/${influencer.id}/influencer-payee-information`}>
              <Button size="sm" color="secondary">
                View
              </Button>
            </Link>
          ) : undefined,
          archive: showArchiveButton ? (
            <Button
              size="sm"
              color="secondary"
              onClick={() => {
                archiveInfluencer(influencer.id, !isArchived, params);
              }}
            >
              {isArchived ? 'Unarchive' : 'Archive'}
            </Button>
          ) : undefined,
        } satisfies InfluencerRow;
      }),
    [rows, currentUser.role, archiveInfluencer, params],
  );

  return (
    <DataGrid
      hideFilterButton
      hideGroupButton
      columns={columns}
      loading={loading}
      rows={transformedRows}
      initialState={{
        pinnedColumns: {
          left: ['influencer_name'],
        },
      }}
      sortingMode="server"
      paginationMode="server"
      rowCount={count || 0}
      {...datagridProps}
    />
  );
}

export default SocialMediaDataGrid;
